export const getTeamExternalId = (matchDetails, side) => {
    if (process.env.REACT_APP_LEAGUE_NAME === 'nhl') {
        return matchDetails[side + 'TeamShortName'];
    } else {
        return matchDetails[side + 'TeamFeedId'];
    }
}

// Per-sport stat definitions for the fixture Stats JSON, shared by the match details view and
// manual scoring. Keys match what the feed sync functions write and the scoring strategies in
// nhl-ultimate-fan-core-functions read.
//   editable: can be edited in manual scoring (the stat is read by scoring)
//   targets:  extra keys that receive the same value when edited
//   compute:  display-only value derived from the player's other stats
const SPORT_STATS = {
    hockey: {
        team: [
            { key: "Goals", label: "Goals", editable: true },
            { key: "GoalsAgainst", label: "Goals Against" },
            { key: "Saves", label: "Saves", editable: true },
            { key: "PowerPlayGoals", label: "Power Play Goals", editable: true },
            { key: "ShortHandedGoals", label: "Short Handed Goals", editable: true },
        ],
        player: [
            { key: "Goals", label: "G", editable: true },
            { key: "Assists", label: "A", editable: true },
            { key: "ShotsOnGoal", label: "SOG", editable: true },
            { key: "Hits", label: "HIT", editable: true },
            { key: "BlockedShots", label: "BLK", editable: true },
        ],
    },
    soccer: {
        team: [
            { key: "Goals", label: "Goals", editable: true },
            { key: "GoalsAgainst", label: "Goals Against" },
            { key: "Saves", label: "Saves", editable: true },
            { key: "Corners", label: "Corners", editable: true },
            { key: "Shutouts", label: "Clean Sheet" },
        ],
        player: [
            { key: "Goals", label: "G", editable: true },
            { key: "Assists", label: "A", editable: true },
            { key: "ShotsOnGoal", label: "SOG", editable: true },
            { key: "Tackles", label: "T", editable: true },
            { key: "PassInterceptions", label: "PI", editable: true },
        ],
    },
    football: {
        team: [
            { key: "Score", label: "Points", editable: true },
            { key: "PointsAllowed", label: "Points Allowed" },
            { key: "Touchdowns", label: "Touchdowns" },
            { key: "TotalYards", label: "Total Yards" },
            { key: "PassingYards", label: "Passing Yards" },
            { key: "RushingYards", label: "Rushing Yards" },
            { key: "FirstDowns", label: "First Downs" },
            { key: "Sacks", label: "Sacks", editable: true },
            { key: "DefensiveInterceptions", label: "Interceptions", editable: true },
            { key: "DefensiveTouchdowns", label: "Defensive TDs", editable: true },
            { key: "FieldGoalsMade", label: "Field Goals" },
            { key: "FieldGoalsMade40to49", label: "Field Goals 40-49", editable: true },
            { key: "FieldGoalsMade50Plus", label: "Field Goals 50+", editable: true },
        ],
        player: [
            { key: "PassingYards", label: "PA YDS", editable: true },
            { key: "PassingTouchdowns", label: "PA TD", editable: true },
            { key: "RushingYards", label: "RU YDS", editable: true },
            { key: "RushingTouchdowns", label: "RU TD", editable: true },
            { key: "Receptions", label: "REC", editable: true },
            { key: "ReceivingYards", label: "RE YDS", editable: true },
            { key: "ReceivingTouchdowns", label: "RE TD", editable: true },
            { key: "InterceptionsThrown", label: "INT", editable: true },
            { key: "FumblesLost", label: "FL", editable: true },
        ],
    },
    basketball: {
        team: [
            // Team win compares Goals vs GoalsAgainst and 100+ points reads Score
            { key: "Points", label: "Points", editable: true, targets: ["Goals", "Score"] },
            { key: "GoalsAgainst", label: "Points Against" },
            { key: "Rebounds", label: "Rebounds" },
            { key: "Assists", label: "Assists" },
            { key: "Steals", label: "Steals" },
            { key: "Blocks", label: "Blocks", editable: true },
            { key: "ThreePointers", label: "Three Pointers", editable: true },
            { key: "Turnovers", label: "Turnovers", editable: true },
        ],
        player: [
            { key: "Points", label: "PTS", editable: true },
            { key: "Rebounds", label: "REB", editable: true },
            { key: "Assists", label: "AST", editable: true },
            { key: "Steals", label: "STL", editable: true },
            { key: "Blocks", label: "BLK", editable: true },
            { key: "Turnovers", label: "TO", editable: true },
            { key: "ThreePointers", label: "3PM", editable: true },
        ],
    },
    australian_football: {
        team: [
            { key: "Goals", label: "Goals", editable: true },
            { key: "GoalsAgainst", label: "Goals Against" },
            { key: "Tackles", label: "Tackles", editable: true },
            { key: "Marks", label: "Marks", editable: true },
        ],
        player: [
            { key: "Kicks", label: "K", editable: true },
            { key: "Handballs", label: "HB", editable: true },
            { key: "Tackles", label: "T", editable: true },
            { key: "Hitouts", label: "HO", editable: true },
            { key: "Goals", label: "G", editable: true },
            // The feed writes FreeKickAgainst but scoring reads FreeKicksAgainst
            { key: "FreeKickAgainst", label: "FKA", editable: true, targets: ["FreeKicksAgainst"] },
        ],
    },
    rugby: {
        team: [
            { key: "Score", label: "Score", editable: true },
            { key: "ScoreAgainst", label: "Score Against" },
            { key: "Tries", label: "Tries", editable: true },
            { key: "Tackles", label: "Tackles", editable: true },
            { key: "LineBreaks", label: "Line Breaks", editable: true },
            { key: "Goals", label: "Goals" },
        ],
        player: [
            { key: "Tries", label: "T", editable: true },
            { key: "TryAssists", label: "TA", editable: true },
            { key: "LineBreaks", label: "LB", editable: true },
            { key: "Tackles", label: "TK", editable: true },
            { key: "TackleBreaks", label: "TB", editable: true },
            { key: "Goals", label: "G", editable: true },
        ],
    },
    cricket: {
        team: [
            { key: "Runs", label: "Runs", editable: true },
            { key: "RunsAgainst", label: "Runs Against" },
            { key: "Wickets", label: "Wickets", editable: true },
            { key: "Sixes", label: "Sixes", editable: true },
        ],
        player: [
            { key: "Runs", label: "R", editable: true },
            { key: "Fours", label: "4s", editable: true },
            { key: "Sixes", label: "6s", editable: true },
            { key: "Wickets", label: "W", editable: true },
            { key: "ThreeWickets", label: "3W", compute: (player) => (player.Wickets >= 3 ? 1 : 0) },
            { key: "FiftyPlusRuns", label: "50+ R", compute: (player) => (player.Runs >= 50 ? 1 : 0) },
        ],
    },
};

const getSportStats = (sportType) => SPORT_STATS[sportType] || SPORT_STATS.hockey;

export const getMatchStats = (sportType) => getSportStats(sportType).team;

export const getPlayerStats = (sportType) => getSportStats(sportType).player;


export const getPlayerStatValue = (stat, player) =>
    stat.compute ? stat.compute(player) : player[stat.key] ?? 0;
